import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import { DropdownItem } from 'reactstrap';
import Isvg from 'react-inlinesvg';
import history from 'app/routes/history';
import IconModal from 'app/components/general/IconModal';
import Table from 'app/components/general/Table';
import ModalConfirm from 'app/components/general/ModalConfirm';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import StatusBadge from 'app/components/general/StatusBadge';
import PlusIcon from 'app/images/icons/plus.svg';
import DeleteIcon from 'app/images/icons/delete.svg';
import EditIcon from 'app/images/icons/edit.svg';
import moment from 'moment';

import { unformatAmount, getTimeRange, formatAmount } from 'app/utils/parsers';

import { getPayoutSummary } from 'app/ducks/payouts/selectors';
import { IPayout } from 'app/ducks/payouts/types';

interface IProps {
  actions: any;
  payoutsById: IPayout[];
  payoutIds: string[];
  teamId?: string;
  headerTitle?: string;
  counterText?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  showChildren?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  selectPayout: (value) => void;
}

const statusBadges = (owingNumber, overdueNumber): React.ReactNode[] => {
  const statuses = [];

  if (owingNumber > 0) {
    statuses.push(
      <StatusBadge
        paymentText="owing"
        numberOfObjects={owingNumber}
      />,
    );
  }
  if (overdueNumber > 0) {
    statuses.push(
      <StatusBadge
        paymentText="overdue"
        numberOfObjects={overdueNumber}
      />,
    );
  }
  return statuses;
};

const renderStatus = (cellProps: {
  value: {
    owingNumber: number;
    overdueNumber: number;
  };
}): React.ReactNode => (<div className="payment-badge-group">{statusBadges(cellProps.value.owingNumber, cellProps.value.overdueNumber)}</div>);

const PayoutsTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  payoutsById: undefined,
  payoutIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  showChildren: true,
  pagination: undefined,
  counterText: undefined,
  selectPayout: undefined,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [payoutId, setPayoutId] = useState(undefined);

  const filterOptions = [
    { label: 'Name', accessor: 'fullName', type: 'text' },
    { label: 'Team', accessor: 'teamName', type: 'text' },
    { label: 'Email', accessor: 'email', type: 'text' },
    { label: 'Mobile', accessor: 'mobile', type: 'text' },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllPayouts(payload);
  };

  const handleOpenModal = (e, selectedPayoutId): void => {
    e.preventDefault();
    setDeleteModalOpen(true);
    setPayoutId(selectedPayoutId);
  };

  const handeCloseModal = (e): void => {
    e.preventDefault();
    setDeleteModalOpen(false);
    setPayoutId(undefined);
  };

  const handleOpenEditModal = (e, selectedPayoutId): void => {
    e.preventDefault();
    props.actions.selectPayout(selectedPayoutId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = (e): void => {
    e.preventDefault();
    setEditModalOpen(false);
  };

  const handleOpenCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(true);
  };

  const handeCloseCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(false);
  };

  const handleDelete = (e): void => {
    e.preventDefault();
    props.actions.deletePayout({ id: payoutId });
    setDeleteModalOpen(false);
  };

  const renderDeleteModal = (): React.ReactNode => (
    <ModalConfirm
      title="Delete Team Member"
      icon={DeleteIcon}
      description="Are you sure you want to delete this team member?"
      buttonText="Delete"
      isOpen={deleteModalOpen}
      isLoading={props.isLoading}
      handleSubmit={(e) => handleDelete(e)}
      handleCancel={handeCloseModal}
    />
  );

  const handleClick = (id: string, original, e): void => {
    // props.actions.selectPayout(id);
    history.push(`/payouts/detail/${id}`);
    e.stopPropagation();
    handleOpenEditModal(e, id);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.payoutsById) || props.payoutIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.payoutIds,
      (obj, id) => {
        if (!_.isEmpty(props.payoutsById[id])) {
          obj.push(props.payoutsById[id]);
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.payoutsById, props.payoutIds]);

  const renderAmount = (cellProps: {
    value: string;
  }): React.ReactNode => (
    formatAmount(cellProps.value)
  );

  const renderDate = (cellProps: {
    value: string;
  }): string => (_.isEmpty(cellProps.value) ? '' : moment(cellProps.value).format('D MMM YYYY'));

  const smallColumn = 'col col-3 col-lg-2';
  const growColumn = 'col col-4 d-flex';
  const mediumColumn = 'col col-4 col-md-3 col-lg-3 col-xl-2';

  const columns = useMemo(() => [
    {
      Header: 'Date',
      accessor: 'createdAt',
      className: smallColumn,
      headerClassName: smallColumn,
      Cell: renderDate,
    },
    {
      Header: 'Amount',
      accessor: 'amountInDollars',
      className: smallColumn,
      headerClassName: smallColumn,
      Cell: renderAmount,
    },
    {
      Header: '# of Payments',
      accessor: 'numberOfPayments',
      className: growColumn,
      headerClassName: growColumn,
    },
    {
      Header: 'Bank Reference',
      accessor: 'reference',
      className: growColumn,
      headerClassName: growColumn,
    },
  ], []);

  // const counterText = props.pagination.meta.totalCount === 1 ? '1 payout' : `${props.pagination.meta.totalCount || 0} team members`;

  return (
    <div>
      {renderDeleteModal()}
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        headerTitle={props.headerTitle}
        objectTitle="payouts"
        filterTitle="Payouts Data"
        counterText={props.counterText}
        handleClick={handleClick}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
      />
    </div>
  );
};

export default PayoutsTable;
